/**
 * The gaming day, for code that runs in a BROWSER.
 *
 * `packages/db/src/gaming-day.ts` is the definition, and it is the only one —
 * but it imports `pg` and cannot be loaded in a client bundle. So the constant
 * lives here too, in the one package both sides already depend on, rather than
 * being retyped in each app. `apps/back-office/src/features/dashboard/PeriodBI.tsx`
 * had already declared its own copy with the comment "Mirrors GAMING_DAY_TIMEZONE
 * in packages/db" — a mirror is exactly what drifts, and it is the reason
 * gaming-day.ts was written in the first place.
 *
 * WHY THIS FILE EXISTS AT ALL, rather than every screen calling toLocaleDateString.
 *
 * `toLocaleDateString('en-PH')` sets the FORMAT and says nothing about the
 * ZONE, so it renders in whatever timezone the viewer's browser is in. Ten
 * places in the back office did exactly that, and they read as correct because
 * the locale is Philippine. They are not: between 16:00 and 24:00 UTC — which
 * is midnight to 08:00 in Manila, the tail of the casino night — a browser
 * anywhere west of the Philippines prints the PREVIOUS gaming day. An operator
 * in London and an operator in Manila reading the same screen saw different
 * dates over the same figures.
 */

export const GAMING_DAY_TIMEZONE = 'Asia/Manila';

/** "03/09/2026" — a date, always on the gaming day's own clock. */
const dayFormat = new Intl.DateTimeFormat('en-PH', { timeZone: GAMING_DAY_TIMEZONE });

/** "1:47:03 am" — a time of day, always on the gaming day's own clock. */
const timeFormat = new Intl.DateTimeFormat('en-PH', {
  timeZone: GAMING_DAY_TIMEZONE,
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
});

/** The Manila calendar date of an instant. Accepts an ISO string or a Date. */
export const gamingDate = (at: string | Date): string =>
  dayFormat.format(typeof at === 'string' ? new Date(at) : at);

/** The Manila wall-clock time of an instant. */
export const gamingTime = (at: string | Date): string =>
  timeFormat.format(typeof at === 'string' ? new Date(at) : at);

/** "2026-09-03" — a gaming day as the `YYYY-MM-DD` a date input speaks. */
const isoDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: GAMING_DAY_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * The gaming day an instant falls in, as `YYYY-MM-DD` — the browser's twin of `gamingDayIso` in
 * `packages/db/src/gaming-day.ts`, held to it on every day of a year at both sides of midnight by
 * `services/api/src/__tests__/gaming-day-client-agrees.test.ts`.
 *
 * What a date the console sends must be: the server reads a typed `YYYY-MM-DD` as one of its
 * gaming days, so an export dialog that filled in a UTC date offered "today" as the day before
 * from 00:00 to 08:00 Manila time. Built from the formatter's parts, so the shape does not depend
 * on how a browser punctuates the locale's date.
 */
export function gamingDayOf(at: string | Date): string {
  const parts = isoDayFormat.formatToParts(typeof at === 'string' ? new Date(at) : at);
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}

/**
 * The gaming day `days` days after the one `at` falls in — before it, when `days` is negative —
 * as `YYYY-MM-DD`. `at` is now unless given. What an export dialog fills its dates in with.
 *
 * Stepped in exact 24-hour units: Manila keeps no daylight saving, so twenty-four hours after any
 * instant is the same moment of the next gaming day. Stepping the browser's own calendar instead
 * would move with whatever zone the laptop is set to.
 */
export function gamingDayOffset(days: number, at: string | Date = new Date()): string {
  const instant = typeof at === 'string' ? new Date(at) : at;
  return gamingDayOf(new Date(instant.getTime() + days * 86_400_000));
}
